import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import Offer from '../pages/Offer';

// import PrivacyPolicy from 'src/pages/dashboard/PrivacyPolicy';

// import PlanComparison from 'src/pages/PlanComparison';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },
    { path: 'privacy-policy', element: <PrivacyPolicy /> },
    { path: 'Cancellation-Refund-Policy', element: <CancellationRefund /> },
    { path: 'terms-of-services', element: <TermsofServices /> },
    { path: 'offer', element: <Offer /> },
    { path: 'price', element: <Price /> },
    { path: 'payment/success', element: <PaymentSuccess /> },
    { path: 'payment/cancle', element: <PaymentCancle /> },
    { path: 'homeRegister', element: <HomeRegister /> },
    { path: 'submitForm', element: <SubmitForm /> },
    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <Gst /> },
        {
          path: 'setting',

          children: [
            { element: <Settings />, index: true },
            { path: 'profile-setting', element: <ProfileSetting /> },
            { path: 'sales-setting', element: <SalesSettings /> },
            { path: 'transport-setting', element: <TransportSettings /> },
            { path: 'air-transport-setting', element: <AirTransportSettings /> },
            { path: 'jewellery-setting', element: <JewellerySettings /> },
            { path: 'purchase-setting', element: <PurchaseSettings /> },
            { path: 'expense-setting', element: <ExpenseSetting /> },
            { path: 'job-project-setting', element: <JobProjectSetting /> },
            { path: 'tax-setting', element: <TaxSetting /> },
            // { path: 'receipt-payment-setting', element: <ReceiptSetting /> },
            { path: 'whatsup-setting', element: <WhatsUpSetting /> },
            { path: 'email-setting', element: <EmailSetting /> },
            { path: 'industries-setting', element: <IndustriesSetting /> },
          ],
        },
        {
          path: 'income-tax',
          children: [
            {
              path: 'income-tax-list',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <IncomeTax />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'tds',
          children: [
            {
              path: 'tds-list',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Tds />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        {
          path: 'audit',
          children: [
            {
              path: 'audit-list',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Audit />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'roc',
          children: [
            {
              path: 'roc-list',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Roc />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        {
          path: 'party',
          children: [
            {
              path: 'party-list',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Party />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        {
          path: 'branches',
          children: [
            {
              path: 'branch',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Branches />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new-branch',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewBranches />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'importdata',
          children: [{ path: 'import-data-form', element: <ImportForm /> }],
        },
        {
          path: 'master-data',
          children: [
            { element: <Navigate to="/dashboard/master-data" replace />, index: true },
            {
              path: 'ledger-party',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <LedgerParty />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new-ledger-party',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewLedgerParty />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'edit-ledger-party/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewLedgerParty />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'ledger-details/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <LedgerDetails />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'inventory-master',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <InventoryMaster />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new-inventory-master',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewInventoryMaster />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'edit-inventory-master/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewInventoryMaster />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'service-master',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <ServiceMaster />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new-service-master',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewServiceMaster />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'edit-service-master/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewServiceMaster />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        {
          path: 'sales-panel',
          children: [
            {
              path: 'user',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <SalesPanel />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new-user',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewSalesPanel />
                </RoleBasedGuard>
              ),
            },

            {
              path: 'edit-user/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewSalesPanel />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'organisation',
          children: [
            {
              path: 'transport',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Transport />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new-transport',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewTransport />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new-transport-form',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewTransporForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'edit-transport-form/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewTransporForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'air-transport',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <AirTransport />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'air-transport-form',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <AirTransporForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'edit-air-transport-form/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <AirTransporForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'duplicate-air-transport-form/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <AirTransporForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'jewellery',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Jewellery />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'jewellery-form',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <JewelleryForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'edit-jewellery-form/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <JewelleryForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'duplicate-jewellery-form/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <JewelleryForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'courier',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Courier />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        {
          path: 'payment',
          children: [
            {
              path: '',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Payment />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new-payment',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewPayment />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'edit-payment/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewPayment />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'view-payment/:id',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <NewPayment />
                </RoleBasedGuard>
              ),
            },
            { path: 'new-expense', element: <NewExpense /> },
            { path: 'edit-expense/:id', element: <NewExpense /> },
            { path: 'view-expense/:id', element: <NewExpense /> },
            { path: 'duplicate-expense/:id', element: <NewExpense /> },
          ],
        },

        {
          path: 'purchase',
          children: [
            { path: '', element: <Purchase /> },
            { path: 'new-purchase', element: <NewPurchase /> },
            { path: 'edit-purchase/:id', element: <NewPurchase /> },
            { path: 'duplicate-purchase/:id', element: <NewPurchase /> },
            { path: 'view-purchase/:id', element: <NewPurchase /> },
            { path: 'purchase-bill', element: <PurchaseBill /> },
            { path: 'edit-purchase-bill/:id', element: <PurchaseBill /> },
            { path: 'duplicate-purchase-bill/:id', element: <PurchaseBill /> },
            { path: 'view-purchase-bill/:id', element: <PurchaseBill /> },
            { path: 'purchase-export', element: <PurchaseExport /> },
            { path: 'duplicate-purchase-export/:id', element: <PurchaseExport /> },
            { path: 'edit-purchase-export/:id', element: <PurchaseExport /> },
            { path: 'view-purchase-export/:id', element: <PurchaseExport /> },
            { path: 'purchase-order', element: <PurchaseOrder /> },
            { path: 'edit-purchase-order/:id', element: <PurchaseOrder /> },
            { path: 'duplicate-purchase-order/:id', element: <PurchaseOrder /> },
            { path: 'view-purchase-order', element: <PurchaseOrder /> },
            { path: 'InwardDeliveryChallan', element: <InwardDeliveryChallan /> },
            { path: 'edit-InwardDeliveryChallan/:id', element: <InwardDeliveryChallan /> },
            { path: 'duplicate-InwardDeliveryChallan/:id', element: <InwardDeliveryChallan /> },
            { path: 'view-InwardDeliveryChallan/:id', element: <InwardDeliveryChallan /> },
          ],
        },
        {
          path: 'banking',
          children: [
            {
              path: '',
              element: (
                <RoleBasedGuard accessibleRoles={['admin']}>
                  <Banking />
                </RoleBasedGuard>
              ),
            },
            { path: 'newbankingtable', element: <BankingStatementTable /> },
            { path: ' bankingform', element: <BankingForm /> },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        // { path: 'terms-of-services', element: <TermsofServices /> },
        // { path: 'privacy-policy', element: <PrivacyPolicy /> },
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'pricing-plan', element: <PlanComparison /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { element: <HomePage />, index: true },
        { path: 'about-us', element: <About /> },
        { path: 'pricing-plans', element: <Price /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> },
      ],
    },
    // { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
// Dashboard
const TermsofServices = Loadable(lazy(() => import('../pages/dashboard/TermsofServices')));
const PrivacyPolicy = Loadable(lazy(() => import('../pages/dashboard/PrivacyPolicy')));
const CancellationRefund = Loadable(lazy(() => import('../pages/dashboard/CancellationRefund')));
// const Landing = Loadable(lazy(() => import('../pages/Landing')));
// const Offer = Loadable(lazy(() => import('../pages/Offer')));
const Price = Loadable(lazy(() => import('../pages/PricingPlans')));
const Gst = Loadable(lazy(() => import('../pages/dashboard/Gst')));

const HomeRegister = Loadable(lazy(() => import('../pages/dashboard/HomeRegister')));
const SubmitForm = Loadable(lazy(() => import('../pages/dashboard/SubmitForm')));
const Audit = Loadable(lazy(() => import('../pages/dashboard/ComingPage')));
const IncomeTax = Loadable(lazy(() => import('../pages/dashboard/ComingPage')));
const Roc = Loadable(lazy(() => import('../pages/dashboard/ComingPage')));
const Tds = Loadable(lazy(() => import('../pages/dashboard/ComingPage')));
const Party = Loadable(lazy(() => import('../pages/dashboard/Party')));

// const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
// const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
// const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
// const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));
// const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
// const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
// const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/InventoryMaster')));
// const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
// const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));
// const EcommerceInvoice = Loadable(lazy(() => import('../pages/dashboard/EcommerceInvoice')));
// const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
// const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
// const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));
const User = Loadable(lazy(() => import('../pages/dashboard/User')));
const NewUser = Loadable(lazy(() => import('../pages/dashboard/NewUser')));
const SalesPanel = Loadable(lazy(() => import('../pages/dashboard/SalesPanel')));
const NewSalesPanel = Loadable(lazy(() => import('../pages/dashboard/NewSalesPanel')));
const ImportForm = Loadable(lazy(() => import('../sections/@dashboard/importdata/ImportForm')));
// const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
// const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
// const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
// const UserCreate = Loadable(lazy(() => import('../pages/dashboard/NewInventoryMaster')));
// const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
// const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
// const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
// const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));
// master data
const LedgerParty = Loadable(lazy(() => import('../pages/dashboard/LedgerParty')));
const NewLedgerParty = Loadable(lazy(() => import('../pages/dashboard/NewLedgerParty')));
const LedgerDetails = Loadable(lazy(() => import('../pages/dashboard/LedgerDetails')));
const InventoryMaster = Loadable(lazy(() => import('../pages/dashboard/InventoryMaster')));
const NewInventoryMaster = Loadable(lazy(() => import('../pages/dashboard/NewInventoryMaster')));
const ServiceMaster = Loadable(lazy(() => import('../pages/dashboard/ServiceMaster')));
const NewServiceMaster = Loadable(lazy(() => import('../pages/dashboard/NewServiceMaster')));
// settings
const Settings = Loadable(lazy(() => import('../pages/dashboard/Settings')));
const ProfileSetting = Loadable(lazy(() => import('../pages/dashboard/ProfileSetting')));
const SalesSettings = Loadable(lazy(() => import('../pages/dashboard/SalesSettings')));
const TaxSetting = Loadable(lazy(() => import('../pages/dashboard/tax/TaxSetting')));
const PurchaseSettings = Loadable(lazy(() => import('../pages/dashboard/PurchaseSettings')));
const JobProjectSetting = Loadable(lazy(() => import('../pages/dashboard/JobProjectSetting')));
const WhatsUpSetting = Loadable(lazy(() => import('../pages/dashboard/WhatsUpSetting')));
const EmailSetting = Loadable(lazy(() => import('../pages/dashboard/EmailSetting')));
const IndustriesSetting = Loadable(lazy(() => import('../pages/dashboard/IndustriesSetting')));
const NewTransporForm = Loadable(
  lazy(() => import('../sections/@dashboard/organisation/newtransport/NewTransportForm'))
);
const NewTransport = Loadable(lazy(() => import('../pages/dashboard/NewTransport')));
const TransportSettings = Loadable(lazy(() => import('../pages/dashboard/TransportSetting')));
const AirTransportSettings = Loadable(lazy(() => import('../pages/dashboard/AirTransportSetting')));
const JewellerySettings = Loadable(lazy(() => import('../pages/dashboard/JewellerySetting')));
const AirTransport = Loadable(lazy(() => import('../pages/dashboard/AirTransport')));
const AirTransporForm = Loadable(
  lazy(() => import('../sections/@dashboard/organisation/airtransport/NewTransportForm'))
);
const Jewellery = Loadable(lazy(() => import('../pages/dashboard/Jewellery')));
const JewelleryForm = Loadable(lazy(() => import('../sections/@dashboard/organisation/jewellery/NewTransportForm')));
// branch
const Branches = Loadable(lazy(() => import('../pages/dashboard/Branches')));
const NewBranches = Loadable(lazy(() => import('../pages/dashboard/NewBranches')));

// organisation
const Transport = Loadable(lazy(() => import('../pages/dashboard/Transport')));
const Courier = Loadable(lazy(() => import('../pages/dashboard/Courier')));

// sales

const Banking = Loadable(lazy(() => import('../pages/dashboard/Banking')));
const BankingStatementTable = Loadable(lazy(() => import('../sections/@dashboard/Banking/BankingStatementTable')));
const BankingForm = Loadable(lazy(() => import('../sections/@dashboard/Banking/BankingForm')));

// payment
const Payment = Loadable(lazy(() => import('../pages/dashboard/Payment')));
const NewPayment = Loadable(lazy(() => import('../pages/dashboard/NewPayment')));
// expense

const NewExpense = Loadable(lazy(() => import('../pages/dashboard/NewExpenseOrJournal')));
const ExpenseSetting = Loadable(lazy(() => import('../pages/dashboard/ExpenseSetting')));

// purchase
const Purchase = Loadable(lazy(() => import('../pages/dashboard/Purchase')));
const NewPurchase = Loadable(lazy(() => import('../pages/dashboard/NewPurchase')));
const PurchaseBill = Loadable(lazy(() => import('../pages/dashboard/PurchaseBill')));
const PurchaseExport = Loadable(lazy(() => import('../pages/dashboard/PurchaseImport')));
const PurchaseOrder = Loadable(lazy(() => import('../pages/dashboard/PurchaseOrder')));
const InwardDeliveryChallan = Loadable(lazy(() => import('../pages/dashboard/InwardDeliveryChallan')));
// job

// const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
// const InvoiceDetails = Loadable(lazy(() => import('../pages/dashboard/InvoiceDetails')));
// const InvoiceCreate = Loadable(lazy(() => import('../pages/dashboard/InvoiceCreate')));
// const InvoiceEdit = Loadable(lazy(() => import('../pages/dashboard/InvoiceEdit')));
// others

// Main
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const PlanComparison = Loadable(lazy(() => import('../pages/PlanComparison')));
// const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

const PaymentSuccess = Loadable(lazy(() => import('../pages/payment/Success')));
const PaymentCancle = Loadable(lazy(() => import('../pages/payment/Cancle')));
